/**
 * Command Center — the new admin landing page.
 *
 * Heartbeat of THE ARCHIVE. Aggregates live signals from existing endpoints
 * into a compact, scannable grid. Auto-refresh is opt-in (30s).
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, RefreshCw, Shield, Users, FileSearch, AlertTriangle,
  Radio, History, Cpu, ChevronRight, CheckCircle2, Loader2, Ban, Lock,
} from "lucide-react";
import {
  adminListLeads, adminAuditLog, adminSecurityStats, adminSecurityLockouts,
} from "../../lib/api";
import { MissionBriefing } from "./MissionBriefing";
import { GraphHealthTile } from "./GraphHealthTile";

const Tile = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`border border-[#2D2A26] bg-[#0F0F0F] p-4 ${className}`}
  >
    {children}
  </div>
);

const TileHeader = ({ icon: Icon, label, count, to, color = "#D92D20" }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-1.5">
      <Icon size={11} style={{ color }} />
      <span className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color }}>
        {label}
      </span>
      {typeof count === "number" && count > 0 && (
        <span className="ml-1 px-1.5 py-0.5 border font-mono text-[8px]" style={{ color, borderColor: `${color}80` }}>
          {count}
        </span>
      )}
    </div>
    {to && (
      <Link
        to={to}
        className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#E4DFD3] flex items-center gap-0.5"
      >
        Open <ChevronRight size={9} />
      </Link>
    )}
  </div>
);

const Stat = ({ label, value, tone, sub }) => {
  const colorByTone = {
    danger: "#D92D20", warn: "#E0A526", ok: "#5DD4B3", info: "#9FA8D4",
  };
  const c = colorByTone[tone] || "#E4DFD3";
  return (
    <div>
      <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065]">
        {label}
      </div>
      <div className="font-display text-2xl" style={{ color: c }}>
        {value === undefined || value === null ? "—" : value}
      </div>
      {sub && <div className="font-mono text-[9px] text-[#757065]">{sub}</div>}
    </div>
  );
};

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

export const CommandCenter = ({ opToken, userToken }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, audit, secStats, lockouts] = await Promise.allSettled([
        adminListLeads(opToken, {}, userToken),
        adminAuditLog(opToken, userToken, 8),
        adminSecurityStats(opToken, userToken),
        adminSecurityLockouts(opToken, true, userToken),
      ]);
      setData({
        submissions: subs.status === "fulfilled"
          ? (subs.value?.leads || subs.value || [])
          : [],
        audit: audit.status === "fulfilled" ? (audit.value?.events || audit.value || []) : [],
        secStats: secStats.status === "fulfilled" ? secStats.value : null,
        lockouts: lockouts.status === "fulfilled" ? (lockouts.value?.lockouts || []) : [],
        live: null,
        audience: null,
      });
      setLastRefresh(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  }, [opToken, userToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const subs = data.submissions || [];
  const pendingCount = subs.filter((s) =>
    !["published", "archived", "rejected"].includes(s.workflow_status),
  ).length;
  const recent = [...subs].sort(
    (a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0),
  ).slice(0, 5);

  const activeLockouts = (data.lockouts || []).filter((l) => l.is_active);

  return (
    <div className="space-y-6" data-testid="admin-command-center">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 border-l-2 border-[#D92D20] pl-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={14} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              // OPERATIONS · COMMAND CENTER
            </span>
          </div>
          <h1 className="font-display text-3xl text-[#E4DFD3]">
            Live status of THE ARCHIVE
          </h1>
          <p className="font-mono text-[11px] text-[#757065] mt-1">
            One pane of glass for what&apos;s happening across submissions, audience, and security.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93]">
            <input
              data-testid="cmd-auto-refresh"
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-[#D92D20]"
            />
            Auto-refresh 30s
          </label>
          <button
            data-testid="cmd-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Refresh All
          </button>
        </div>
      </div>

      {/* Mission Briefing — AI summary of the entire snapshot */}
      <MissionBriefing
        opToken={opToken}
        userToken={userToken}
        refreshTick={lastRefresh}
      />

      {/* Top stat band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cmd-stat-band">
        <Tile testid="cmd-stat-pending">
          <Stat label="Pending Investigations" value={pendingCount}
                tone={pendingCount > 0 ? "warn" : "ok"} />
        </Tile>
        <Tile testid="cmd-stat-live">
          <Stat
            label="Live Visitors (now)"
            value={data.live?.active_now ?? data.live?.live_count ?? "—"}
            tone="info"
            sub={data.live?.peak_today ? `peak ${data.live.peak_today}` : null}
          />
        </Tile>
        <Tile testid="cmd-stat-members">
          <Stat
            label="Active Members"
            value={data.audience?.active_members ?? data.audience?.total_members ?? "—"}
            tone="ok"
            sub={data.audience?.new_today ? `+${data.audience.new_today} today` : null}
          />
        </Tile>
        <Tile testid="cmd-stat-security">
          <Stat
            label="Security · Last 1h"
            value={data.secStats?.last_1h ?? 0}
            tone={data.secStats?.last_1h > 0 ? "danger" : "ok"}
            sub={`${data.secStats?.active_lockouts ?? 0} active lockouts`}
          />
        </Tile>
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Recent submissions */}
        <Tile className="lg:col-span-2" testid="cmd-recent-submissions">
          <TileHeader icon={FileSearch} label="Recent Investigations" to="/admin/dossiers/submissions" count={recent.length} />
          {recent.length === 0 ? (
            <div className="font-mono text-[10px] text-[#757065] italic">// No submissions yet.</div>
          ) : (
            <ul className="divide-y divide-[#2D2A26]/60">
              {recent.map((s) => (
                <li key={s.id} className="py-2 flex items-center justify-between gap-2" data-testid={`cmd-sub-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-[#E4DFD3] truncate">
                      {s.title || s.topic || s.draft_summary?.headline || "(untitled)"}
                    </div>
                    <div className="font-mono text-[9px] text-[#757065] truncate">
                      {s.submitter || s.requester_email || "anon"} · {relTime(s.updated_at || s.created_at)}
                    </div>
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#CBB892] shrink-0">
                    {s.workflow_status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Tile>

        {/* Security alerts */}
        <Tile testid="cmd-security-alerts">
          <TileHeader icon={Shield} label="Security Alerts" to="/admin/security/failed-access"
                       count={activeLockouts.length} />
          <div className="space-y-1.5">
            <div className="flex items-center justify-between font-mono text-[10px]">
              <span className="text-[#A39E93]">Failed last 24h</span>
              <span className="text-[#E4DFD3]">{data.secStats?.last_24h ?? 0}</span>
            </div>
            <div className="flex items-center justify-between font-mono text-[10px]">
              <span className="text-[#A39E93]">Active lockouts</span>
              <span className={activeLockouts.length ? "text-[#D92D20]" : "text-[#5DD4B3]"}>
                {activeLockouts.length}
              </span>
            </div>
            <div className="flex items-center justify-between font-mono text-[10px]">
              <span className="text-[#A39E93]">Expired (24h)</span>
              <span className="text-[#A39E93]">{data.secStats?.expired_lockouts ?? 0}</span>
            </div>
          </div>
          {activeLockouts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#2D2A26]">
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D92D20] mb-1.5 flex items-center gap-1">
                <Lock size={9} /> Currently locked
              </div>
              <ul className="space-y-1">
                {activeLockouts.slice(0, 3).map((l) => (
                  <li key={l.fingerprint} className="flex items-center justify-between font-mono text-[9px] text-[#E4DFD3]">
                    <span>{l.ip_address || (l.fingerprint || "").slice(0, 12) + "…"}</span>
                    <span className="text-[#E0A526]">T{l.offense_tier}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Tile>

        {/* Recent admin activity */}
        <Tile className="lg:col-span-2" testid="cmd-admin-activity">
          <TileHeader icon={History} label="Recent Admin Activity" to="/admin/security/audit" />
          {(data.audit || []).length === 0 ? (
            <div className="font-mono text-[10px] text-[#757065] italic">// No recent activity.</div>
          ) : (
            <ul className="divide-y divide-[#2D2A26]/60">
              {(data.audit || []).slice(0, 6).map((ev, i) => (
                <li key={ev.id || i} className="py-1.5 flex items-center justify-between gap-2 font-mono text-[10px]">
                  <div className="min-w-0">
                    <span className="text-[#E4DFD3]">{ev.event}</span>
                    <span className="text-[#757065] ml-2">{ev.actor_email || "system"}</span>
                  </div>
                  <span className="text-[#757065] shrink-0">{relTime(ev.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </Tile>

        {/* Quick Actions */}
        <Tile testid="cmd-quick-actions">
          <TileHeader icon={Cpu} label="Quick Actions" />
          <div className="grid grid-cols-1 gap-1.5">
            <QuickAction to="/admin/dossiers/submissions" icon={FileSearch}>Triage submissions</QuickAction>
            <QuickAction to="/admin/dossiers/review" icon={CheckCircle2}>Open review folder</QuickAction>
            <QuickAction to="/admin/dossiers/news-radar" icon={Radio}>News radar</QuickAction>
            <QuickAction to="/admin/members/live" icon={Activity}>Live audience</QuickAction>
            <QuickAction to="/admin/security/failed-access" icon={Shield}>Security console</QuickAction>
            <QuickAction to="/admin/settings/site" icon={Cpu}>Site settings</QuickAction>
          </div>
        </Tile>

        {/* System Health */}
        <Tile className="lg:col-span-2" testid="cmd-system-health">
          <TileHeader icon={CheckCircle2} label="System Health" color="#5DD4B3" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <HealthRow label="Backend" status="ok" detail={`updated ${lastRefresh ? relTime(lastRefresh) : "—"}`} />
            <HealthRow label="Database" status={data.audit ? "ok" : "warn"} detail="Mongo · indexed" />
            <HealthRow label="Auth Pipeline" status={data.secStats ? "ok" : "warn"} detail="hardened · obfuscated" />
            <HealthRow
              label="Permanent Blocks"
              status={data.secStats?.active_lockouts > 5 ? "warn" : "ok"}
              detail={`${activeLockouts.length} active`}
            />
          </div>
        </Tile>

        {/* Oracle Knowledge Graph — auto-refresh health.
            Answers "is the intelligence network current?" without opening logs. */}
        <GraphHealthTile opToken={opToken} userToken={userToken} />
      </div>
    </div>
  );
};

const QuickAction = ({ to, icon: Icon, children }) => (
  <Link
    to={to}
    data-testid={`cmd-qa-${(children || "").toString().replace(/\s/g, "-").toLowerCase()}`}
    className="flex items-center justify-between gap-2 px-2.5 py-1.5 border border-[#2D2A26] hover:border-[#D92D20] hover:bg-[#1a0808] font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#E4DFD3]"
  >
    <span className="flex items-center gap-1.5">
      <Icon size={11} /> {children}
    </span>
    <ChevronRight size={11} />
  </Link>
);

const HealthRow = ({ label, status, detail }) => {
  const colors = { ok: "#5DD4B3", warn: "#E0A526", danger: "#D92D20" };
  const c = colors[status] || "#A39E93";
  return (
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c, boxShadow: `0 0 8px ${c}` }} />
      <div className="min-w-0">
        <div className="font-mono text-[10px] text-[#E4DFD3] uppercase tracking-[0.2em]">{label}</div>
        <div className="font-mono text-[9px] text-[#757065]">{detail}</div>
      </div>
    </div>
  );
};

export default CommandCenter;
